import React from "react";
import { InputWrapper } from "../../../../components/Input/InputWrapper";
import { InputLabel } from "../../../../components/Input/InputLabel";
import { CustomInput } from "../../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../../components/Input/CustomPhoneInput";
import {
  FieldGrid,
  FieldGroup,
} from "../../../../components/OrgShell/FieldGroup";
import { nameOf } from "../../../../../utils/functions/ReflectionUtil";
import { CustomInputChangeEvent } from "../../../../../utils/models/events/CustomInputChangeEvent";
import { WhiteLabelConfigurationsForm } from "../white-label-editor.components";
import { ColorPickerEditorWrapper } from "../color-picker-editor.wrapper";
import { OrganizationLogoPicker } from "../organization-logo-picker";

interface BrandingSectionProps {
  form: WhiteLabelConfigurationsForm;
  onFormChange: (form: WhiteLabelConfigurationsForm) => void;
  onLogoChange: (file: File) => void;
  onLogoRemove?: () => void;
  onCancelRemove?: () => void;
  logoUrl?: string;
  pendingUploadPreview?: string;
  pendingRemoval?: boolean;
  disabled: boolean;
  /** Shown when the section is locked for a sub-branch. */
  lockedByName?: string;
}

/** The white-label editor content presented as a shell section. */
export function BrandingSection({
  form,
  onFormChange,
  onLogoChange,
  onLogoRemove,
  onCancelRemove,
  logoUrl,
  pendingUploadPreview,
  pendingRemoval,
  disabled,
  lockedByName,
}: BrandingSectionProps) {
  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    onFormChange({ ...form, [target.name]: target.value });
  }

  return (
    <div className="space-y-8">
      {disabled && (
        <p className="rounded-md border border-orange-200 bg-orange-50 px-4 py-2.5 text-xs text-orange-700">
          Branding is managed by {lockedByName || "your main branch"}.
        </p>
      )}

      <FieldGroup
        title="White label"
        description="How the platform is branded for your customers."
      >
        <FieldGrid cols={3}>
          <div>
            <InputWrapper>
              <InputLabel>Company Display Name</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (f) => f.whiteLabelVendorName
                )}
                value={form.whiteLabelVendorName}
                onChange={handleInput}
                disabled={disabled}
              />
            </InputWrapper>
            <ColorPickerEditorWrapper
              form={form}
              onFormChange={onFormChange}
              disabled={disabled}
            />
            <InputWrapper>
              <InputLabel>Website Link</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (f) => f.whiteLabelWebsiteLink
                )}
                value={form.whiteLabelWebsiteLink}
                onChange={handleInput}
                disabled={disabled}
              />
            </InputWrapper>
          </div>
          <div>
            <InputWrapper>
              <InputLabel>Email</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (f) => f.whiteLabelContactEmail
                )}
                value={form.whiteLabelContactEmail}
                onChange={handleInput}
                disabled={disabled}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Phone</InputLabel>
              <CustomPhoneInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (f) => f.whiteLabelContactPhone
                )}
                value={form.whiteLabelContactPhone}
                onChange={handleInput}
                disabled={disabled}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Copyright Name</InputLabel>
              <CustomInput
                name={nameOf<WhiteLabelConfigurationsForm>(
                  (f) => f.whiteLabelCopyrightName
                )}
                value={form.whiteLabelCopyrightName}
                onChange={handleInput}
                disabled={disabled}
              />
            </InputWrapper>
          </div>
          <div>
            <OrganizationLogoPicker
              value={logoUrl ?? ""}
              onLogoChange={onLogoChange}
              onLogoRemove={onLogoRemove}
              onCancelRemove={onCancelRemove}
              disabled={disabled}
              pendingUploadPreview={pendingUploadPreview}
              pendingRemoval={pendingRemoval}
            />
          </div>
        </FieldGrid>
      </FieldGroup>
    </div>
  );
}
